// services/astro-bot-miniapp/src/screens/onboarding/SelfieIntro.tsx
// OB5γ.1 SelfieIntro — camera-icon-in-circle header, two CTAs (Take photo / Upload from gallery),
// 3 privacy bullets with lock/calendar/star icons.
// B.4: Consent A modal shown before navigating to capture. Skip CTA advances onboardingStep.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OnboardingProgressBar } from '../../components/onboarding/OnboardingProgressBar.js';
import { SelfieConsentModal, hasCurrentBiometricConsent } from '../../components/avatarFlow/SelfieConsentModal.js';
import { useMe } from '../../hooks/useMe.js';
import { useAuth } from '../../store/auth.js';
import { apiFetch } from '../../lib/api-client.js';
import { ONBOARDING_STEPS } from '../../lib/onboarding-steps.js';
import styles from './SelfieIntro.module.css';

function CameraIcon(): JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CalendarIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function StarIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2" />
    </svg>
  );
}

function ReassuranceLine({ icon, text }: { icon: JSX.Element; text: string }): JSX.Element {
  return (
    <li className={styles.line}>
      <span className={styles.lineIcon} aria-hidden="true">{icon}</span>
      <span>{text}</span>
    </li>
  );
}

export function SelfieIntro(): JSX.Element {
  const { t } = useTranslation('flows');
  const nav = useNavigate();
  const jwt = useAuth(s => s.jwt);
  const setStep = useAuth(s => s.setOnboardingStep);
  const { data: me } = useMe();

  // Whether we're showing the Consent A modal and what navigation to perform after
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingDest, setPendingDest] = useState<string | null>(null);
  const [skipping, setSkipping] = useState(false);

  function requestNavigate(dest: string): void {
    // If user already has current consent, navigate immediately
    if (hasCurrentBiometricConsent(me)) {
      nav(dest, dest === '/onboarding/capture' ? {} : {});
      return;
    }
    // Otherwise show Consent A first
    setPendingDest(dest);
    setConsentOpen(true);
  }

  function openGallery(): void {
    if (hasCurrentBiometricConsent(me)) {
      nav('/onboarding/capture', { state: { openGallery: true } });
      return;
    }
    setPendingDest('gallery');
    setConsentOpen(true);
  }

  function handleConsentAccept(): void {
    setConsentOpen(false);
    if (pendingDest === 'gallery') {
      nav('/onboarding/capture', { state: { openGallery: true } });
    } else if (pendingDest) {
      nav(pendingDest);
    }
    setPendingDest(null);
  }

  function handleConsentDecline(): void {
    setConsentOpen(false);
    setPendingDest(null);
  }

  async function handleSkip(): Promise<void> {
    if (skipping) return;
    setSkipping(true);
    try {
      if (jwt) {
        // Advance onboardingStep to SELFIE_PENDING (6) on the server
        await apiFetch('/api/v1/me', jwt, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ onboardingStep: ONBOARDING_STEPS.SELFIE_PENDING }),
        });
        setStep(ONBOARDING_STEPS.SELFIE_PENDING);
      }
    } catch {
      // navigate anyway even on failure
    } finally {
      nav('/onboarding/adding', { replace: true });
    }
  }

  return (
    <div className={styles.screen}>
      <CosmosBackdrop />

      {/* Consent A modal — shown before navigating to capture */}
      <SelfieConsentModal
        open={consentOpen}
        jwt={jwt}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />

      <div className={styles.content}>
        <OnboardingProgressBar step={5} etaSeconds={20} />

        {/* Camera-icon-in-circle header */}
        <div className={styles.iconCircle} aria-hidden="true">
          <CameraIcon />
        </div>

        <h1 className={styles.title}>{t('onboarding.selfieIntro.header')}</h1>
        <p className={styles.body}>{t('onboarding.selfieIntro.body')}</p>

        <ul className={styles.reassurance}>
          <ReassuranceLine icon={<LockIcon />} text={t('onboarding.selfieIntro.privacy.a')} />
          <ReassuranceLine icon={<CalendarIcon />} text={t('onboarding.selfieIntro.privacy.b')} />
          <ReassuranceLine icon={<StarIcon />} text={t('onboarding.selfieIntro.privacy.c')} />
        </ul>
      </div>

      <div className={styles.ctaArea}>
        <button
          type="button"
          className={styles.ctaPrimary}
          onClick={() => requestNavigate('/onboarding/capture')}
        >
          {t('onboarding.selfieIntro.cta.take')}
        </button>
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={openGallery}
        >
          {t('onboarding.selfieIntro.cta.upload')}
        </button>
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={() => void handleSkip()}
          disabled={skipping}
          data-testid="selfie-intro-skip"
        >
          {t('onboarding.selfieIntro.cta.skip')}
        </button>
      </div>
    </div>
  );
}
